import { useEffect, useRef, useState } from "react";
import Portal from "@Components/Portal";
import CloseIcon from "@Icons/CloseIcon";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./EmailDialog.module.scss";

interface EmailDialogProps {
  /** The bare address, without `mailto:`. */
  email: string;
  onClose: () => void;
}

/** Two overlapping sheets — drawn in `currentColor`, so it follows the button's text colour. */
const CopyGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="8.5" y="8.5" width="12" height="12" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M15.5 5.5V5a2.5 2.5 0 0 0-2.5-2.5H6A2.5 2.5 0 0 0 3.5 5v7A2.5 2.5 0 0 0 6 14.5h.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/** Shown in place of the copy glyph once the address is on the clipboard. */
const CheckGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** How long "Copied" shows before the button reads "Copy email" again. */
const COPIED_MS = 2000;

/**
 * Puts an address on the clipboard. The async Clipboard API first; where that is missing or
 * refused (older Safari, a non-secure origin, a denied permission) the old select-and-copy path,
 * which works almost everywhere a user gesture is behind it.
 */
const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch (e) {
    return false;
  }
};

/**
 * "Email us", as a choice rather than a jump.
 *
 * ⚠️  WHY THIS EXISTS: a bare `mailto:` link does nothing visible for a lot of people — no mail
 * app configured, a browser or company policy that blocks the handler, or a webmail user whose
 * browser opens an empty desktop client. The reader clicks, nothing happens, and they assume the
 * link is broken. So the link opens this instead: the address in full, a button that copies it,
 * and the `mailto:` still there as a real link for everyone it does work for.
 *
 * Opened by `Navbar` for any nav link whose `href` is a `mailto:`, so every v2 page gets it by
 * passing its email link the way it already did.
 */
const EmailDialog = ({ email, onClose }: EmailDialogProps) => {
  const { t } = useTranslationHook();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyRef = useRef<HTMLButtonElement>(null);
  const addressRef = useRef<HTMLSpanElement>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  // The latest `onClose`, read at call time. Callers pass an inline arrow, so depending on it
  // directly would tear down and re-run the effect below on every parent render — re-reading
  // the "opener" mid-dialog and bouncing focus.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc closes; Tab stays inside the dialog; focus moves in on open and goes back to whatever
  // opened it on close; the page behind stops scrolling while it is up. Runs once per open.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    copyRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      // ⚠️  A MODAL MUST TRAP TAB. Without this, Tab walks out into the page behind, which is
      // still interactive — a keyboard user could trigger nav links while the dialog is up.
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>("button, a[href]");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      // The opener lives in the page, not the portal, so it survives the unmount. Checked
      // anyway: if it was removed while the dialog was up, focusing it would drop to <body>.
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(id);
  }, [copied]);

  const handleCopy = async () => {
    const ok = await copyText(email);
    setCopied(ok);
    setCopyFailed(!ok);
    // Nothing could reach the clipboard: select the address so a long-press or Cmd+C still works.
    if (!ok && addressRef.current) {
      const range = document.createRange();
      range.selectNodeContents(addressRef.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  };

  return (
    // Not `renderOnDidMount`: this only ever mounts from a click, so `#forPortal` is already in
    // the page, and rendering the card in the same pass is what lets the effect above focus it.
    <Portal rootId="forPortal">
      {/* Clicking the dimmed backdrop closes; clicks inside the card land on the card, not
        on this element, so they are ignored rather than stopped. */}
      <div
        className={styles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        role="presentation"
      >
        <div
          ref={dialogRef}
          className={styles.dialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-dialog-title"
        >
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t("emaildialog.close")}
          >
            <CloseIcon />
          </button>

          <h2 id="email-dialog-title" className={styles.title}>
            {t("home.contactus.emailus")}
          </h2>
          <p className={styles.hint}>{t("emaildialog.hint")}</p>

          <span className={styles.address} ref={addressRef}>
            {email}
          </span>

          <div className={styles.actions}>
            <button
              type="button"
              ref={copyRef}
              className={`${styles.button} ${styles.primary}`}
              onClick={handleCopy}
              aria-live="polite"
            >
              {copied ? <CheckGlyph /> : <CopyGlyph />}
              {copied ? t("emaildialog.copied") : t("emaildialog.copy")}
            </button>
            <a className={`${styles.button} ${styles.secondary}`} href={`mailto:${email}`}>
              {t("emaildialog.open")}
            </a>
          </div>

          {copyFailed && <p className={styles.fallback}>{t("emaildialog.copyfailed")}</p>}
        </div>
      </div>
    </Portal>
  );
};

export default wrapperHOC(EmailDialog, {
  componentName: "EmailDialog",
  showForChina: true,
});
